(() => {
  if (window.NubsStoreBrowseNavigation) return;

  let controller = null;
  let requestSerial = 0;

  // Shopify filters variants. Assemble the product-level sold-out result set
  // before choosing the visible page, using Shopify-rendered cards.
  const pageSize = 24;
  const batchSize = 50;
  const soldOutCache = new Map();

  async function readBatch(url, page, signal) {
    const target = new URL(url);
    target.searchParams.set('page', String(page));
    const response = await fetch(target, {signal, credentials: 'same-origin'});
    if (!response.ok) throw new Error('Shopify could not load availability results.');
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const batch = doc.querySelector('template[data-nubs-sold-out-batch]');
    const pages = Number(batch?.dataset.pages);
    if (!batch || Number(batch.dataset.page) !== page ||
        !Number.isInteger(pages) || pages < 0 || pages > Math.ceil(25000 / batchSize)) {
      throw new Error('Shopify returned an invalid availability page.');
    }
    return {pages, cards: [...batch.content.querySelectorAll('article[data-nubs-catalog-handle]')]};
  }

  async function readCards(target, signal) {
    const url = new URL(target);
    url.hash = '';
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.searchParams.delete('nubs_sort');
    if (url.searchParams.get('sort_by')?.startsWith('release-date-')) url.searchParams.delete('sort_by');
    url.searchParams.set('view', 'nubs-sold-out');

    const key = url.href;
    const cached = soldOutCache.get(key);
    if (cached && Date.now() - cached.time < 15000) return cached.cards;

    const first = await readBatch(url, 1, signal);
    const batches = [first.cards];
    for (let start = 2; start <= first.pages; start += 4) {
      const work = [];
      for (let page = start; page <= Math.min(start + 3, first.pages); page++) {
        work.push(readBatch(url, page, signal));
      }
      for (const batch of await Promise.all(work)) {
        if (batch.pages !== first.pages) throw new Error('The availability results changed while loading.');
        batches.push(batch.cards);
      }
    }
    const cards = batches.flat();
    const handles = new Set(cards.map(card => card.dataset.nubsCatalogHandle));
    if (handles.size !== cards.length) throw new Error('The availability results changed while loading.');
    if (soldOutCache.size >= 4) soldOutCache.delete(soldOutCache.keys().next().value);
    soldOutCache.set(key, {time: Date.now(), cards});
    return cards;
  }

  function renderPager(grid, url, kind, page, totalPages) {
    if (totalPages <= 1) return;
    const doc = grid.ownerDocument;
    const pager = doc.createElement('nav');
    pager.className = `nubs-${kind}-pagination`;
    pager.setAttribute('aria-label', kind === 'search' ? 'Search result pages' : 'Collection pages');
    const addLink = (label, number) => {
      const target = new URL(url);
      target.searchParams.set('page', String(number));
      const link = doc.createElement('a');
      link.className = `nubs-${kind}-pagination__link`;
      link.href = target.href;
      link.dataset.nubsSoldOutPage = '';
      link.textContent = label;
      pager.append(link);
    };
    if (page > 1) addLink('← Previous', page - 1);
    const position = doc.createElement('span');
    position.className = `nubs-${kind}-pagination__status`;
    position.textContent = `Page ${page} of ${totalPages}`;
    pager.append(position);
    if (page < totalPages) addLink('Next →', page + 1);
    grid.after(pager);
  }

  async function prepareAvailability(page, target, signal) {
    const status = page?.querySelector('[data-nubs-sold-out-status]');
    if (!page?.hasAttribute('data-nubs-sold-out-pending') || !status) return;
    const kind = page.hasAttribute('data-nubs-search') ? 'search' : 'collection';
    const grid = page.querySelector(`.nubs-${kind}-results__grid`);
    const stillCurrent = () => !signal?.aborted && page.contains(status);
    try {
      let cards = await readCards(target, signal);
      const sort = target.searchParams.get('nubs_sort') || target.searchParams.get('sort_by');
      let sortUnavailable = false;
      if (kind === 'collection' && ['release-date-newest', 'release-date-oldest'].includes(sort) && cards.length > 1) {
        try {
          cards = await window.NubsShopRelease.sortSoldOut(cards, sort, signal);
        } catch (error) {
          if (signal?.aborted) throw error;
          sortUnavailable = true;
        }
      }
      if (!stillCurrent()) return;

      const total = cards.length;
      const totalPages = Math.ceil(total / pageSize);
      const requestedPage = Number.parseInt(target.searchParams.get('page') || '1', 10) || 1;
      const current = Math.min(Math.max(1, requestedPage), Math.max(1, totalPages));
      const first = (current - 1) * pageSize;
      grid.replaceChildren(...cards.slice(first, first + pageSize).map(card => grid.ownerDocument.importNode(card, true)));
      grid.hidden = !total;
      const image = grid.querySelector('img');
      if (image) {
        image.loading = 'eager';
        image.fetchPriority = 'high';
      }
      renderPager(grid, target, kind, current, totalPages);
      const stats = page.querySelector(`.nubs-${kind}-page__stats`);
      const count = page.querySelector(`.nubs-${kind}-page__result-count`);
      const stat = stats?.querySelector('strong');
      const label = stat?.nextElementSibling;
      if (stat) stat.textContent = String(total);
      if (label) label.textContent = total === 1 ? 'Sold-out title' : 'Sold-out titles';
      if (stats) stats.hidden = false;
      if (count) {
        count.textContent = `${total} ${total === 1 ? 'item' : 'items'}`;
        count.hidden = false;
      }
      const facetCount = page.querySelector('[data-nubs-sold-out-count]');
      if (facetCount) {
        facetCount.textContent = String(total);
        facetCount.hidden = false;
      }
      page.removeAttribute('data-nubs-sold-out-pending');
      page.dataset.nubsSoldOutReady = '';
      status.hidden = total > 0 && !sortUnavailable;
      status.textContent = !total ? 'No fully sold-out titles match these filters.' :
        sortUnavailable ? 'Release-date sorting is temporarily unavailable. Showing sold-out titles in the default order.' : '';
    } catch (error) {
      if (!stillCurrent()) return;
      status.textContent = 'Could not check sold-out titles. ';
      const retry = status.ownerDocument.createElement('a');
      retry.href = target.href;
      retry.dataset.nubsHardNavigation = '';
      retry.textContent = 'Try again';
      status.append(retry);
      console.warn('[Nub Shop] Availability results unavailable.', error);
    }
  }

  for (const event of ['nubs:cart-changed', 'nubs:session-cleared']) {
    document.addEventListener(event, () => soldOutCache.clear());
  }

  const sectionIdFor = (page) => {
    const explicit = String(page?.dataset?.nubsSectionId || '').trim();
    if (explicit) return explicit;
    const wrapper = page?.closest?.('.shopify-section[id]');
    return String(wrapper?.id || '').replace(/^shopify-section-/, '');
  };

  const openFilterKeys = (page) => new Set(
    [...(page?.querySelectorAll?.('details[open][data-nubs-filter-param]') || [])]
      .map((detail) => String(detail.dataset.nubsFilterParam || '').trim())
      .filter(Boolean)
  );

  const restoreOpenFilterKeys = (page, keys) => {
    if (!page || !keys?.size) return;
    page.querySelectorAll('details[data-nubs-filter-param]').forEach((detail) => {
      if (keys.has(String(detail.dataset.nubsFilterParam || '').trim())) detail.open = true;
    });
  };

  const formUrl = (form) => {
    const url = new URL(form?.action || window.location.href, window.location.origin);
    url.search = '';
    const data = new FormData(form);
    for (const [key, rawValue] of data.entries()) {
      const value = String(rawValue ?? '').trim();
      if (!value) continue;
      url.searchParams.append(key, value);
    }
    return url;
  };

  const liveForm = '[data-nubs-store-query]';
  let liveTimer;
  let liveController;

  function cancelLiveSearch() {
    clearTimeout(liveTimer);
    liveController?.abort();
    liveController = null;
  }

  function liveSearchUrl(page, form, query) {
    const current = new URL(location.href);
    const target = formUrl(form);
    const filters = page.querySelector('#NubsSearchFilterForm');
    if (page.hasAttribute('data-nubs-search')) {
      const params = filters ? formUrl(filters).searchParams : current.searchParams;
      for (const [key, value] of params) {
        if (key.startsWith('filter.') || key === 'sort_by') target.searchParams.append(key, value);
      }
    }
    if (query) {
      if (!target.searchParams.has('filter.v.availability')) {
        try {
          if (sessionStorage.getItem('nubs-store-availability-mode') !== 'all') {
            target.searchParams.set('filter.v.availability', '1');
          }
        } catch (_) {
          target.searchParams.set('filter.v.availability', '1');
        }
      }
      target.searchParams.set('q', query);
    } else {
      target.searchParams.delete('q');
    }
    const preview = current.searchParams.get('preview_theme_id');
    if (preview) target.searchParams.set('preview_theme_id', preview);
    return target;
  }

  function bindLiveSearch(page, render) {
    const inputFor = form => form?.querySelector('input[name="q"]');
    const queue = (input, automatic) => {
      cancelLiveSearch();
      const form = input.form;
      form.querySelector('[data-nubs-live-search-error]')?.remove();
      const query = input.value.trim();
      const current = new URL(location.href);
      if (!query && (!page.hasAttribute('data-nubs-search') || !current.searchParams.get('q'))) return;
      const target = liveSearchUrl(page, form, query);
      const next = new URL(target);
      next.searchParams.sort();
      current.searchParams.sort();
      if (next.href === current.href) return;
      controller?.abort();

      const request = new AbortController();
      liveController = request;
      const run = async () => {
        if (!input.isConnected || request.signal.aborted) return;
        const historyMode = history.state?.nubsLiveStoreSearch && page.hasAttribute('data-nubs-search')
          ? 'replace' : 'push';
        try {
          const result = await render(target, historyMode, {input, signal: request.signal, automatic});
          if (result === false && !request.signal.aborted && input.isConnected) {
            const message = document.createElement('small');
            message.dataset.nubsLiveSearchError = '';
            message.setAttribute('role', 'status');
            message.textContent = 'Search could not load. Try again or press Enter.';
            input.after(message);
          }
        } finally {
          if (liveController === request) liveController = null;
        }
      };
      if (automatic) liveTimer = setTimeout(run, 160);
      else run();
    };

    page.addEventListener('input', event => {
      const form = event.target.closest(liveForm);
      if (event.target === inputFor(form) && !event.isComposing) queue(event.target, true);
    });
    page.addEventListener('compositionstart', event => {
      if (event.target === inputFor(event.target.closest(liveForm))) cancelLiveSearch();
    });
    page.addEventListener('compositionend', event => {
      if (event.target === inputFor(event.target.closest(liveForm))) queue(event.target, true);
    });
    page.addEventListener('submit', event => {
      if (event.defaultPrevented || !event.target.matches(liveForm)) return;
      const input = inputFor(event.target);
      if (!input) return;
      event.preventDefault();
      queue(input, false);
    });

    // The first query can move from a collection into the search route.
    // Pick up any typing that happened while the new page's script initialized.
    const input = inputFor(page.querySelector(liveForm));
    if (input && input.value.trim() !== input.defaultValue.trim()) queue(input, true);
  }

  // Keep the focused form connected on consecutive searches, including on mobile.
  function replaceAround(current, incoming) {
    const parent = current.parentNode;
    const before = [];
    const after = [];
    let siblings = before;
    for (const sibling of incoming.parentNode.childNodes) {
      if (sibling === incoming) siblings = after;
      else siblings.push(document.importNode(sibling, true));
    }
    for (const child of [...parent.childNodes]) if (child !== current) child.remove();
    current.before(...before);
    current.after(...after);
  }

  function restoreQueryFocus(root, input) {
    if (document.activeElement !== input) return () => {};
    const {value, selectionStart, selectionEnd, selectionDirection} = input;
    return () => {
      const next = root.querySelector(liveForm + ' input[name="q"]');
      if (!next) return;
      next.value = value;
      next.focus({preventScroll: true});
      if (selectionStart !== null) next.setSelectionRange(selectionStart, selectionEnd, selectionDirection);
    };
  }

  function replaceSearchSection(page, nextPage, input) {
    if (!input) {
      page.innerHTML = nextPage.innerHTML;
      return;
    }
    const nextForm = nextPage.querySelector(liveForm);
    const currentForm = input.form;
    const header = currentForm?.parentElement;
    if (nextForm && header?.parentElement === page &&
        nextForm.parentElement?.parentElement === nextPage &&
        header.className === nextForm.parentElement.className) {
      replaceAround(currentForm, nextForm);
      replaceAround(header, nextForm.parentElement);
      const renderedInput = nextForm.querySelector('input[name="q"]');
      if (renderedInput) input.defaultValue = renderedInput.defaultValue;
      return;
    }
    const restoreFocus = restoreQueryFocus(page, input);
    page.innerHTML = nextPage.innerHTML;
    restoreFocus();
  }

  window.addEventListener('popstate', cancelLiveSearch);
  document.addEventListener('nubs:beforepagechange', cancelLiveSearch);

  const update = async (page, target, options = {}) => {
    if (!page) return false;
    const {
      selector,
      historyMode = 'push',
      preserveScroll = true,
      afterRender,
      liveSearch,
    } = options;

    if (!liveSearch) cancelLiveSearch();
    const cleanUrl = target instanceof URL ? new URL(target.toString()) : new URL(String(target), window.location.origin);
    cleanUrl.searchParams.delete('section_id');

    const sectionId = sectionIdFor(page);
    if (!sectionId || !selector) {
      window.location.assign(cleanUrl.toString());
      return false;
    }

    if (liveSearch?.signal.aborted) return false;
    controller?.abort();
    const requestController = new AbortController();
    controller = requestController;
    const serial = ++requestSerial;
    const abort = () => requestController.abort();
    liveSearch?.signal.addEventListener('abort', abort, {once: true});
    const scroller = window.NubsSoftNavigation?.pageScrollElement() || document.scrollingElement || document.documentElement;
    const entry = historyMode === 'none' ? history.state : null;
    const scrollY = entry?.nubsScrollY ?? entry?.scrollTop ?? scroller.scrollTop;
    const scrollX = entry?.nubsScrollX ?? scroller.scrollLeft;
    const openKeys = openFilterKeys(page);

    page.setAttribute('aria-busy', 'true');

    const requestUrl = new URL(cleanUrl.toString());
    requestUrl.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(requestUrl.toString(), {
        signal: requestController.signal,
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });
      if (!response.ok) throw new Error(`Store filter update returned ${response.status}`);

      const html = await response.text();
      if (serial !== requestSerial || requestController.signal.aborted) return false;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const nextPage = parsed.querySelector(selector);
      if (!nextPage) throw new Error('Updated store section could not be found.');

      if (nextPage.hasAttribute('data-nubs-sold-out-pending')) {
        await prepareAvailability(nextPage, cleanUrl, requestController.signal);
        if (serial !== requestSerial || requestController.signal.aborted) return false;
      }

      if (historyMode === 'push') {
        window.history.pushState(liveSearch ? {nubsLiveStoreSearch: true} : {}, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
      } else if (historyMode === 'replace') {
        window.history.replaceState(liveSearch ? {nubsLiveStoreSearch: true} : {}, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
      }

      replaceSearchSection(page, nextPage, liveSearch?.input);
      [...page.attributes]
        .filter((attribute) => attribute.name.startsWith('data-') && !['data-nubs-section-id', 'data-nubs-async-filters-bound'].includes(attribute.name))
        .forEach((attribute) => page.removeAttribute(attribute.name));
      [...nextPage.attributes]
        .filter((attribute) => attribute.name.startsWith('data-') && !['data-nubs-section-id', 'data-nubs-async-filters-bound'].includes(attribute.name))
        .forEach((attribute) => page.setAttribute(attribute.name, attribute.value));
      page.dataset.nubsSectionId = sectionId;

      restoreOpenFilterKeys(page, openKeys);

      window.NubsSoftNavigation?.markStoreSection(cleanUrl);
      if (preserveScroll && historyMode === 'none') {
        scroller.scrollTo({top: scrollY, left: scrollX, behavior: 'instant'});
      }
      await afterRender?.(page, cleanUrl);

      if (preserveScroll && historyMode !== 'none') {
        requestAnimationFrame(() => {
          if (serial === requestSerial) scroller.scrollTo({top: scrollY, left: scrollX, behavior: 'instant'});
        });
      }
      return true;
    } catch (error) {
      if (error?.name === 'AbortError' || requestController.signal.aborted || serial !== requestSerial) return false;
      if (liveSearch?.automatic) return false;
      console.warn("[Nub's Media] Store filter update failed; using normal navigation.", error);
      window.location.assign(cleanUrl.toString());
      return false;
    } finally {
      liveSearch?.signal.removeEventListener('abort', abort);
      if (serial === requestSerial) page.removeAttribute('aria-busy');
    }
  };

  window.NubsStoreBrowseNavigation = {
    formUrl, update, prepareAvailability, bindLiveSearch, restoreQueryFocus,
    cancel({keepLiveSearch = false} = {}) {
      if (!keepLiveSearch) cancelLiveSearch();
      requestSerial++;
      controller?.abort();
    },
  };
})();
